import { getMainApi } from '@elva/platform';
import { logPatientWire } from '../debug/logPatientWire';

function isHttpUrl(value: unknown): value is string {
  return typeof value === 'string' && (value.startsWith('http://') || value.startsWith('https://'));
}

function pickSignedUrl(payload: unknown): string | undefined {
  if (!payload || typeof payload !== 'object') return undefined;
  const record = payload as Record<string, unknown>;
  const nested =
    record.data && typeof record.data === 'object' ? (record.data as Record<string, unknown>) : record;
  const candidates = [
    nested.url,
    nested.signed_url,
    nested.signature_url,
    nested.file_url,
    record.url,
    record.signed_url,
    record.signature_url,
  ];
  return candidates.find(isHttpUrl);
}

async function displayUrlFromBlob(blob: Blob): Promise<string | undefined> {
  if (!blob || blob.size === 0) return undefined;
  const type = blob.type || '';
  if (type.includes('json') || type.startsWith('text/')) {
    try {
      return pickSignedUrl(JSON.parse(await blob.text()));
    } catch {
      return undefined;
    }
  }
  return URL.createObjectURL(blob);
}

async function tryAnonymousFetch(url: string, signal?: AbortSignal): Promise<string | undefined> {
  try {
    const res = await fetch(url, signal ? { signal } : undefined);
    if (!res.ok) return undefined;
    return await displayUrlFromBlob(await res.blob());
  } catch {
    return undefined;
  }
}

async function tryGetBlob(
  url: string,
  params: Record<string, string>,
  signal?: AbortSignal,
): Promise<string | undefined> {
  try {
    const res = await getMainApi().get<Blob>(url, {
      params,
      responseType: 'blob',
      headers: { Accept: '*/*' },
      ...(signal ? { signal } : {}),
    });
    return await displayUrlFromBlob(res.data);
  } catch {
    return undefined;
  }
}

/**
 * `signature_keys` is a storage key, not a URL. Staff JWT never goes on `<img src>`,
 * so fetch the PNG (or a signed URL) through the API client and return a blob:/https src.
 */
export async function resolveStaffSubmissionFileUrl(options: {
  fileKey: string;
  fieldKey?: string;
  accessToken?: string;
  submissionId?: string;
  assignmentId?: string;
  signal?: AbortSignal;
}): Promise<string | undefined> {
  const fileKey = options.fileKey.replace(/^\/+/, '');
  const attempts: Array<{ url: string; params: Record<string, string>; anonymous?: boolean }> = [];

  if (options.accessToken) {
    const token = encodeURIComponent(options.accessToken);
    attempts.push(
      { url: `/api/public/forms/${token}/file`, params: { key: fileKey }, anonymous: true },
      { url: `/api/public/forms/${token}/file`, params: { file_key: fileKey }, anonymous: true },
      { url: `/api/public/forms/${token}/file`, params: { key: fileKey } },
      { url: `/api/forms/patient/${token}/file`, params: { key: fileKey } },
    );
  }
  if (options.fieldKey) {
    attempts.push({
      url: `/api/forms/file`,
      params: { field_key: options.fieldKey, key: fileKey },
    });
  }
  attempts.push({ url: `/api/forms/file`, params: { key: fileKey } });
  if (options.submissionId) {
    const id = encodeURIComponent(options.submissionId);
    attempts.push(
      { url: `/api/forms/submissions/${id}/file`, params: { key: fileKey } },
      { url: `/api/forms/submissions/${id}/file`, params: { file_key: fileKey } },
    );
  }
  if (options.assignmentId) {
    attempts.push({
      url: `/api/forms/assignments/${encodeURIComponent(options.assignmentId)}/file`,
      params: { key: fileKey },
    });
  }

  for (const attempt of attempts) {
    // Each attempt swallows its own failure, so an abort would otherwise just fall through to the next one.
    if (options.signal?.aborted) return undefined;
    const resolved = attempt.anonymous
      ? await tryAnonymousFetch(
          `${attempt.url}?${new URLSearchParams(attempt.params).toString()}`,
          options.signal,
        )
      : await tryGetBlob(attempt.url, attempt.params, options.signal);
    if (resolved) {
      logPatientWire('resolved signature file', {
        url: attempt.url,
        anonymous: Boolean(attempt.anonymous),
        blob: resolved.startsWith('blob:'),
      });
      return resolved;
    }
  }

  logPatientWire('could not resolve signature file', {
    fileKey,
    fieldKey: options.fieldKey,
    hasToken: Boolean(options.accessToken),
    submissionId: options.submissionId,
  });
  return undefined;
}
